import { useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { ArrowRightIcon, SparkleIcon, XIcon } from '@phosphor-icons/react';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Segmented } from '~/components/ui/segmented';
import { ZenDialog, ZenDialogHeader, ZenDialogTitle, ZenEyebrow } from '~/components/ui/zen-dialog';
import {
  useFeatureFlags,
  resolveFeatureFlag,
  ARTIFACT_WORKSPACE_FLAG,
  CHANNEL_SLACK_FLAG,
  CODING_SPEND_FLAG,
  COMPUTE_BLOCKS_FLAG,
  MODEL_PICKER_COMPOSER_FLAG,
  ORG_MCP_SERVERS_FLAG,
  SKETCHES_FLAG,
  SLIDE_DECKS_FLAG,
  WEBSITE_IMPORT_FLAG,
} from '~/hooks/queries/use-feature-flags';
import { BRAND_KIT_FLAG } from '~/lib/feature-flag-names';
import { useUiPreferences, type UiPreferences } from '~/hooks/queries/use-ui-preferences';
import { useDismissReleaseNote } from '~/hooks/mutations/use-ui-preferences-mutations';
import {
  CURRENT_RELEASE_NOTE,
  type ReleaseNote,
  type ReleaseNoteFlag,
  type ReleaseNoteItem,
  type ReleaseNoteLang,
} from './release-note';

/**
 * The flag behind each `ReleaseNoteFlag` key. A flag that is not on for
 * everyone reads with fallback `false`: while flags are loading or failed,
 * the note leaves the line out rather than describe a feature the reader may
 * not have. Sketches is on for every production workspace, and its other
 * readers use `true`, so it does too. Typed as a full record, so a new key
 * cannot ship without its flag.
 */
export const RELEASE_NOTE_FLAGS: Record<ReleaseNoteFlag, { name: string; fallback: boolean }> = {
  sketches: { name: SKETCHES_FLAG, fallback: true },
  channelSlack: { name: CHANNEL_SLACK_FLAG, fallback: false },
  brandKit: { name: BRAND_KIT_FLAG, fallback: false },
  modelPicker: { name: MODEL_PICKER_COMPOSER_FLAG, fallback: false },
  canvas: { name: ARTIFACT_WORKSPACE_FLAG, fallback: false },
  slideDecks: { name: SLIDE_DECKS_FLAG, fallback: false },
  computeBlocks: { name: COMPUTE_BLOCKS_FLAG, fallback: false },
  codingSpend: { name: CODING_SPEND_FLAG, fallback: false },
  orgMcpServers: { name: ORG_MCP_SERVERS_FLAG, fallback: false },
  websiteImport: { name: WEBSITE_IMPORT_FLAG, fallback: false },
};

/** The chrome around the note, in both languages the note is written in. */
const CHROME: Record<
  ReleaseNoteLang,
  {
    region: string;
    context: string;
    open: string;
    dismiss: string;
    gotIt: string;
    admins: string;
    why: string;
    language: string;
  }
> = {
  en: {
    region: "What's new in EDDY",
    language: 'Language',
    context: "what's new",
    open: 'See what changed',
    dismiss: "Don't show this again",
    gotIt: 'Got it',
    admins: 'Admins',
    why: 'Why it matters',
  },
  de: {
    region: 'Neu in EDDY',
    language: 'Sprache',
    context: 'Neues',
    open: 'Was ist neu?',
    dismiss: 'Nicht mehr anzeigen',
    gotIt: 'Verstanden',
    admins: 'Admins',
    why: 'Warum das hilft',
  },
};

const LANG_STORAGE_KEY = 'eddy.whats-new.lang';

/**
 * The note's starting language: the one this browser last picked, else
 * German for a German browser, else English. Storage can throw (private
 * window, blocked site data), which simply means "no remembered choice".
 */
export function initialReleaseNoteLang(
  stored: string | null,
  browserLanguage: string | undefined,
): ReleaseNoteLang {
  if (stored === 'en' || stored === 'de') return stored;
  return browserLanguage?.toLowerCase().startsWith('de') ? 'de' : 'en';
}

function readStoredLang(): string | null {
  try {
    return window.localStorage.getItem(LANG_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeLang(lang: ReleaseNoteLang) {
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // A remembered language is a convenience; without storage it resets.
  }
}

/**
 * FRONT-2512: whether the banner shows. Hidden while the person's
 * preferences are still loading, failed to load, or came from a server that
 * does not know the field yet — otherwise a banner they
 * already closed would flash back on every boot, and a cosmetic notice has
 * no business nagging through a failed read.
 */
export function shouldShowReleaseNote(
  note: ReleaseNote | null,
  prefs: Partial<Pick<UiPreferences, 'dismissedReleaseNoteId'>> | undefined,
): note is ReleaseNote {
  if (!note || !prefs) return false;
  // A server that does not send the field at all (a pod still on the
  // previous release mid-deploy) could not store a close either, so the
  // banner would come straight back: treat it as unknown.
  if (prefs.dismissedReleaseNoteId === undefined) return false;
  return prefs.dismissedReleaseNoteId !== note.id;
}

/**
 * Drops the lines whose feature flag is off for this person, and any
 * section left empty by that, so nobody reads about something they cannot
 * open. `isOn` is the caller's flag reader.
 */
export function visibleReleaseNote(
  note: ReleaseNote,
  isOn: (flag: ReleaseNoteFlag) => boolean,
): ReleaseNote {
  return {
    ...note,
    sections: note.sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => !item.flag || isOn(item.flag)),
      }))
      .filter((section) => section.items.length > 0),
  };
}

/**
 * The "What's new" bar under the app header (FRONT-2512). The text lives
 * in `release-note.ts`; this only decides whether to show it and records
 * the close on the person's account, so it stays closed on every device.
 */
export function WhatsNewBanner() {
  const prefs = useUiPreferences();
  const flags = useFeatureFlags();
  const dismiss = useDismissReleaseNote();
  const navigate = useNavigate();
  const note = CURRENT_RELEASE_NOTE;
  if (!shouldShowReleaseNote(note, prefs.isSuccess ? prefs.data : undefined)) return null;
  const isFlagOn = (flag: ReleaseNoteFlag) => {
    const { name, fallback } = RELEASE_NOTE_FLAGS[flag];
    return resolveFeatureFlag(name, fallback, flags.data, flags.isLoading, flags.isError);
  };
  const shown = visibleReleaseNote(note, isFlagOn);
  return (
    <WhatsNewBannerView
      note={shown}
      onDismiss={() => dismiss.mutate(note.id)}
      onNavigate={(href) => navigate(href)}
      readInitialLang={() => initialReleaseNoteLang(readStoredLang(), navigator.language)}
      onLangChange={storeLang}
    />
  );
}

/**
 * A line's `what`, with its `link` label (when it has one and the text
 * carries it) turned into a link. A plain `<a href>` keeps it working where
 * no router is mounted; `onFollowLink` takes over the click in the app.
 */
export function releaseNoteWhat(
  item: ReleaseNoteItem,
  lang: ReleaseNoteLang,
  onFollowLink?: (href: string) => void,
): ReactNode {
  const text = item.what[lang];
  const at = item.link ? text.indexOf(item.link.label) : -1;
  if (!item.link || at < 0) return text;
  const { label, href } = item.link;
  return (
    <>
      {text.slice(0, at)}
      <a
        href={href}
        onClick={(e) => {
          if (!onFollowLink) return;
          e.preventDefault();
          onFollowLink(href);
        }}
        className="text-[var(--eddy-blue-text)] underline decoration-[var(--eddy-blue-line)] underline-offset-2 transition-colors hover:decoration-current"
      >
        {label}
      </a>
      {text.slice(at + label.length)}
    </>
  );
}

/**
 * The note's own content, apart from the window around it: the Radix portal
 * renders nothing under the static-markup tests, so this is what they render.
 */
export function ReleaseNoteBody({
  note,
  lang,
  onFollowLink,
}: {
  note: ReleaseNote;
  lang: ReleaseNoteLang;
  onFollowLink?: (href: string) => void;
}) {
  const t = CHROME[lang];
  return (
    <div className="mx-auto max-w-[720px] px-6 pb-8 pt-9 sm:px-8" data-lang={lang}>
      <ZenEyebrow>EDDY {note.version}</ZenEyebrow>
      <ZenDialogTitle className="text-balance text-[length:var(--text-2xl)] font-semibold leading-tight tracking-[-0.4px] text-foreground">
        {note.headline[lang]}
      </ZenDialogTitle>
      <p className="mt-3 text-pretty text-[length:var(--text-md)] leading-relaxed text-muted-foreground">
        {note.intro[lang]}
      </p>

      <div className="mt-8 grid gap-8">
        {note.sections.map((section) => (
          <section key={section.title.en} className="min-w-0">
            <h3 className="mb-3 border-b border-border pb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              {section.title[lang]}
            </h3>
            <ul className="grid gap-4">
              {section.items.map((item) => (
                <li key={item.what.en} className="grid gap-1">
                  <p className="text-pretty text-[length:var(--text-base)] font-medium leading-snug text-foreground">
                    {releaseNoteWhat(item, lang, onFollowLink)}
                    {item.adminOnly && (
                      <Badge
                        variant="outline"
                        className="ml-1.5 px-1.5 py-0 align-[1px] text-[10.5px] text-muted-foreground"
                      >
                        {t.admins}
                      </Badge>
                    )}
                  </p>
                  <p className="text-pretty text-[length:var(--text-sm)] leading-relaxed text-muted-foreground">
                    <span className="sr-only">{t.why}: </span>
                    {item.why[lang]}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

/** Presentational half, exported so the static-markup tests can render it. */
export function WhatsNewBannerView({
  note,
  onDismiss,
  readInitialLang = () => 'en',
  onLangChange,
  onNavigate,
}: {
  note: ReleaseNote;
  onDismiss: () => void;
  /** Follows a link inside the note; the window closes first. Not a close of the note. */
  onNavigate?: (href: string) => void;
  /** Read once, on first render. */
  readInitialLang?: () => ReleaseNoteLang;
  onLangChange?: (lang: ReleaseNoteLang) => void;
}) {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState<ReleaseNoteLang>(readInitialLang);
  // Got it closes the window first and records the close only once the
  // close animation is over: the record hides the bar, which unmounts this
  // whole component, window included.
  const dismissOnClose = useRef(false);
  const t = CHROME[lang];
  const chooseLang = (next: ReleaseNoteLang) => {
    setLang(next);
    onLangChange?.(next);
  };

  return (
    <>
      <section
        aria-label={t.region}
        data-testid="whats-new-banner"
        className="shrink-0 rounded-[var(--frame-radius)] border border-[var(--eddy-blue-line)] bg-[var(--eddy-blue-soft)]"
      >
        <div className="flex items-center gap-2 pl-4 pr-1.5">
          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={open}
            onClick={() => setOpen(true)}
            className="group flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-md py-2.5 text-left text-sm text-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <SparkleIcon size={16} weight="fill" className="shrink-0 text-[var(--eddy-blue-text)]" aria-hidden />
            <span className="min-w-0 truncate font-medium">{note.headline[lang]}</span>
            <span className="inline-flex shrink-0 items-center gap-1 font-medium text-[var(--eddy-blue-text)]">
              <span className="max-sm:sr-only">{t.open}</span>
              <ArrowRightIcon
                size={12}
                weight="bold"
                aria-hidden
                className="transition-transform duration-150 ease-out group-hover:translate-x-0.5 motion-reduce:transition-none"
              />
            </span>
          </button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t.dismiss}
            onClick={onDismiss}
            className="relative shrink-0 text-muted-foreground after:absolute after:-inset-1 after:content-[''] hover:bg-[var(--eddy-blue-soft)] hover:text-foreground active:scale-[0.96]"
          >
            <XIcon size={14} aria-hidden />
          </Button>
        </div>
      </section>

      <ZenDialog
        open={open}
        onOpenChange={setOpen}
        maxWidth={880}
        data-testid="whats-new-window"
        onCloseAutoFocus={() => {
          if (!dismissOnClose.current) return;
          dismissOnClose.current = false;
          onDismiss();
        }}
      >
        <ZenDialogHeader
          context={t.context}
          right={
            <Segmented<ReleaseNoteLang>
              size="sm"
              aria-label={t.language}
              value={lang}
              onChange={chooseLang}
              options={[
                { value: 'de', label: 'DE', ariaLabel: 'Deutsch', testId: 'whats-new-lang-de' },
                { value: 'en', label: 'EN', ariaLabel: 'English', testId: 'whats-new-lang-en' },
              ]}
            />
          }
        />
        <div className="min-h-0 flex-1 overflow-y-auto">
          <ReleaseNoteBody
            note={note}
            lang={lang}
            onFollowLink={
              onNavigate
                ? (href) => {
                    setOpen(false);
                    onNavigate(href);
                  }
                : undefined
            }
          />
        </div>
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border px-[22px] py-3">
          <Button
            type="button"
            size="sm"
            onClick={() => {
              dismissOnClose.current = true;
              setOpen(false);
            }}
            className="active:scale-[0.96]"
          >
            {t.gotIt}
          </Button>
        </div>
      </ZenDialog>
    </>
  );
}
